<template>
  <section id="product-batch-country-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.product_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="countries"
        :label="$t('attributes.national_flag')"
        v-slot="props"
      >
        <b-taginput
          :ref="`countries-input-${props.row.code}`"
          v-model="props.row.countryList"
          :data="filteredCountries"
          :maxtags="maxCountry"
          :has-counter="false"
          autocomplete
          keep-first
          field="name"
          icon="label"
          :placeholder="
            props.row.countries.length > maxCountry
              ? '最多只可選6個國旗'
              : '選擇國旗'
          "
          @typing="search => filteringCountries(props.row, search)"
          @input="modified(props.row)"
        >
          <template v-slot="options">
            {{ options.option.english_name }} {{ options.option.name }}
          </template>
          <template #selected="options">
            <b-tag
              v-for="(tag, index) in options.tags"
              :key="index"
              :tabstop="false"
              closable
              @close="
                $refs[`countries-input-${props.row.code}`].removeTag(
                  index,
                  $event
                )
              "
            >
              <p
                style="display: flex; justify-content: center; align-items: center;"
              >
                <img
                  :src="countryTemplateFlagImage(tag.flag)"
                  :alt="$t('attributes.flag')"
                  style="width: 16px; margin-right: 6px;"
                />
                {{ tag.name }}
              </p>
            </b-tag>
          </template>
          <template #empty>
            搜尋不到
          </template>
        </b-taginput>
      </b-table-column>

      <b-table-column
        width="50"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              type="is-info"
              icon-left="restore"
              outlined
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-loading :active.sync="isLoading" :is-full-page="false"></b-loading>
  </section>
</template>

<script>
import { COUNTRY_TEMPLATES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'ProductBatchCountryTable',
  props: {
    products: {
      Type: Array,
      required: true
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractProducts(this.products),

      maxCountry: 6,
      loadingCountries: false,
      countries: [],
      filteredCountries: []
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingCountries
    }
  },
  watch: {
    products(list) {
      this.items = this.extractProducts(list)
    }
  },
  mounted() {
    this.loadCountries()
  },
  methods: {
    async loadCountries() {
      const api = `${COUNTRY_TEMPLATES_API}/list`

      try {
        this.loadingCountries = true
        const res = await this.axios.get(api)
        this.countries = res.data.data.map(c => ({ ...c, code: c.code.trim() }))

        this.items = this.items.map(product => {
          const countryList = product.countries.map(country => {
            return this.countries.find(c => c.code === country)
          })

          return {
            ...product,
            countryList: countryList
          }
        })
      } catch (e) {
        this.countries = []

        notifyService.fetch_error('country', e)
      } finally {
        this.loadingCountries = false
      }
    },
    filteringCountries(product, search) {
      const name = search.trim().toLowerCase()
      this.filteredCountries = this.countries.filter(option => {
        return (
          option.name
            .trim()
            .toLowerCase()
            .includes(name) ||
          option.english_name
            .trim()
            .toLowerCase()
            .includes(name)
        )
      })
    },
    extractProducts(products) {
      return products.map(product => {
        const countries = product.countries ? product.countries.split(',') : []
        const countryList = this.countries
          ? countries.map(country => {
              return this.countries.find(c => c.code === country)
            })
          : []

        return {
          code: product.code,
          name: product.name,
          countries: countries,
          countryList: countryList,

          editing: false,
          dirty: false,
          error: null,
          origin: {
            ...product,
            countries: countries
          }
        }
      })
    },

    modified(product) {
      product.countries = product.countryList.map(c => c.code)
      product.dirty = product.countries !== product.origin.countries

      this.validate(product)
    },
    restore(product) {
      product.countries = [...product.origin.countries]
      product.countryList = product.countries.map(country => {
        return this.countries.find(c => c.code === country)
      })

      product.dirty = false
      product.error = false
    },

    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const product of this.items) {
        if (!this.validate(product)) {
          return false
        }
      }

      return true
    },
    validate(product) {
      product.error = null
      return true
    },
    getData() {
      return this.items
        .filter(product => product.dirty)
        .map(product => ({
          code: product.code,
          countries: product.countries.join(',')
        }))
    },
    rowClass(product) {
      if (product.error) {
        return 'has-background-danger-light'
      } else if (product.dirty) {
        return 'has-background-warning'
      } else {
        return null
      }
    }
  }
}
</script>
